import {
  DoubleOrInfinity,
  DoubleOrInfinitySpecialValueEnum,
  HistogramSeriesViewResponse,
  HistogramSeriesViewResponseTypeEnum,
  type SingleHistogramSeriesView,
} from 'generated/leaderboard-client';
import { isNumber, max } from 'lodash';

import { GetColorFunction } from '@neptune/colors-domain';

import {
  HistogramColumnData,
  HistogramData,
  HistogramSeries,
  HistogramSeriesData,
  HistogramType,
} from './histogram-data';
import { HistogramSource } from './histogram-widget';

export abstract class HistogramSeriesConverter {
  public static histogramSeriesFromApiToDomain =
    (sources: HistogramSource[], getColor: GetColorFunction) =>
    (histogramSeries: HistogramSeriesViewResponse): HistogramData => {
      const { columnsData, maxValuePerSeries } =
        HistogramSeriesConverter.columnsDataAndMaxValuePerSeriesFromApiToDomain(
          histogramSeries.elements,
        );
      return {
        edges: histogramSeries.edges.map(HistogramSeriesConverter.edgesFromApiToDomain),
        type: HistogramSeriesConverter.typeFromApiToDomain(histogramSeries.type),
        series: histogramSeries.elements.map(
          HistogramSeriesConverter.seriesMetadataFromApiToDomain(sources, getColor),
        ),
        columnsData,
        maxValuePerSeries,
      };
    };

  private static edgesFromApiToDomain(edge: DoubleOrInfinity): number {
    if (edge.specialValue === DoubleOrInfinitySpecialValueEnum.MinusInfinity) {
      return -Infinity;
    } else if (edge.specialValue === DoubleOrInfinitySpecialValueEnum.Infinity) {
      return Infinity;
    } else if (edge.value === undefined) {
      throw new Error('Edge value is undefined');
    }

    return edge.value;
  }

  private static typeFromApiToDomain(type: HistogramSeriesViewResponseTypeEnum): HistogramType {
    switch (type) {
      case HistogramSeriesViewResponseTypeEnum.Counting:
        return HistogramType.Counting;
      case HistogramSeriesViewResponseTypeEnum.Density:
        return HistogramType.Density;
      default:
        throw new Error(`Unknown histogram type: ${type}`);
    }
  }

  private static seriesMetadataFromApiToDomain =
    (sources: HistogramSource[], getColor: GetColorFunction) =>
    (element: SingleHistogramSeriesView): HistogramSeries => {
      const entityId = element.customId;
      const source = sources.find((s) => s.entityId === entityId);
      const attribute = source?.attributes[0]?.name;

      if (!source) {
        throw new Error('Failed to find source for entityId: ' + entityId);
      }

      if (!attribute) {
        throw new Error('Attribute not found for entityId: ' + entityId);
      }

      return {
        attribute,
        color: getColor(entityId) || getColor(),
        entityId,
        shortId: source.entityShortId,
        projectIdentifier: source.projectIdentifier,
      };
    };

  private static columnsDataAndMaxValuePerSeriesFromApiToDomain(
    seriesArray: SingleHistogramSeriesView[],
  ): {
    columnsData: HistogramColumnData[];
    maxValuePerSeries: Record<string, number>;
  } {
    return seriesArray.reduce<{
      columnsData: HistogramColumnData[];
      maxValuePerSeries: Record<string, number>;
    }>(
      (acc, { values, customId: entityId }) => {
        values.forEach(({ values, x }) => {
          const seriesData: HistogramSeriesData = { id: entityId, values };
          const column = acc.columnsData.find((column) => column.idx === x);

          if (column) {
            column.series[entityId] = seriesData;
          } else {
            acc.columnsData.push({
              idx: x,
              series: {
                [entityId]: seriesData,
              },
            });
          }

          const maxValueForCurrentStep = max(seriesData.values) ?? -Infinity;
          const currentMaxValue = acc.maxValuePerSeries[entityId] ?? -Infinity;

          if (isNumber(maxValueForCurrentStep) && maxValueForCurrentStep > currentMaxValue) {
            acc.maxValuePerSeries[entityId] = maxValueForCurrentStep;
          }
        });

        return acc;
      },
      { columnsData: [], maxValuePerSeries: {} },
    );
  }
}
